import React from 'react'
import { Link } from 'react-router-dom'
import { Target, ShieldCheck, Layers, Lock, ArrowRight } from 'lucide-react'
import { PageHero, Section } from './_shared'

const PRINCIPLES = [
  {
    icon: Target,
    title: 'Accuracy over features',
    body: 'If a number can’t be computed truthfully, it isn’t shown. No estimates dressed up as facts, no fabricated confidence — the platform would rather hide a figure than mislead.',
  },
  {
    icon: ShieldCheck,
    title: 'Fail closed',
    body: 'When data is incomplete or a feed is unverified, serenDPT surfaces that plainly instead of guessing. Every saved state reflects what actually happened.',
  },
  {
    icon: Layers,
    title: 'One unified view',
    body: 'Holdings across four broker accounts, live ticks, fund research and rebalancing live in a single, calm workspace — not scattered across spreadsheets and tabs.',
  },
  {
    icon: Lock,
    title: 'Your data, your machine',
    body: 'Notes and workspace files are stored on disk; portfolio data lives in your own database. Nothing is sold, profiled, or shared with advertisers.',
  },
]

export default function About() {
  return (
    <>
      <PageHero
        kicker="About"
        title="A disciplined investor’s personal command center."
        lede="serenDPT is a personal trading-intelligence platform — built and run by one investor to bring rigor, calm, and a single source of truth to managing real money across Indian markets."
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/features" className="btn btn-primary inline-flex items-center gap-1.5">
            Explore the features <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact" className="btn btn-secondary">Get in touch</Link>
        </div>
      </PageHero>

      <Section title="Why it exists">
        <div className="grid md:grid-cols-2 gap-6 text-[15px] leading-7 text-ink-600 max-w-4xl">
          <p>
            Most retail tools optimize for engagement — flashing tickers, neon alerts, and
            dashboards that create urgency rather than clarity. serenDPT was built in the
            opposite spirit: a quiet, institutional-grade environment for thinking clearly
            about a portfolio over years, not minutes.
          </p>
          <p>
            It aggregates holdings across Zerodha and AngelOne accounts, streams live prices,
            scores a universe of thousands of mutual-fund schemes down to the genuinely
            investable few, models risk-aware rebalancing, and keeps research and decisions in
            a private knowledge workspace — all in one place, all under your control.
          </p>
        </div>
      </Section>

      <Section title="What it stands for" sub="Four principles the product refuses to compromise on." tone="quiet">
        <div className="grid sm:grid-cols-2 gap-5">
          {PRINCIPLES.map((p) => (
            <div key={p.title} className="card">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center shrink-0">
                  <p.icon className="w-5 h-5 text-accent-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-ink-900">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-ink-500 leading-6">{p.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <div className="card bg-ink-900 border-ink-800 text-center py-12">
          <h2 className="font-display font-semibold text-white text-2xl sm:text-3xl tracking-tight">
            Built for one. Crafted like it’s for thousands.
          </h2>
          <p className="mt-3 text-ink-300 max-w-xl mx-auto leading-7">
            serenDPT isn’t a company or a service you sign up for a plan with — it’s a personal
            platform, held to a professional bar.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/features" className="btn btn-primary inline-flex items-center gap-1.5">
              See what it does <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white/90 ring-1 ring-white/20 hover:bg-white/10 transition-colors">
              Contact
            </Link>
          </div>
        </div>
      </Section>
    </>
  )
}
